// Global in-view and hover video playback coordinator.
// Ensures only one video plays at a time across all views, muted,
// prioritizing hovered videos, then the video closest to the vertical
// center of the viewport when in view screen.

let sharedObserver = null;
const registeredVideos = new Map();
const videoPlaybackTimes = new Map();
let activeVideoId = null;
let scrollListenerAttached = false;
let rafId = null;

function getSharedObserver() {
    if (typeof window === 'undefined' || typeof IntersectionObserver !== 'function') {
        return null;
    }

    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    for (const item of registeredVideos.values()) {
                        if (item.element === entry.target) {
                            item.ratio = entry.intersectionRatio;
                            item.isIntersecting = entry.isIntersecting;
                            break;
                        }
                    }
                }
                scheduleUpdate();
            },
            {
                threshold: [0, 0.25, 0.5, 0.75, 1.0]
            }
        );
    }

    return sharedObserver;
}

export function scheduleUpdate() {
    if (typeof window === 'undefined') return;
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateActiveVideo();
    });
}

function hasOpenModal() {
    if (typeof document === 'undefined') return false;
    return Boolean(
        document.querySelector('[role="dialog"]') ||
        document.querySelector('[data-video-workspace-modal="true"]') ||
        document.querySelector('.modal-open') ||
        document.body.classList.contains('modal-open')
    );
}

export function updateActiveVideo() {
    if (typeof document === 'undefined') return;

    if (document.hidden || hasOpenModal()) {
        if (activeVideoId !== null) {
            activeVideoId = null;
            for (const item of registeredVideos.values()) {
                item.setPlaying(false);
            }
        }
        return;
    }

    // 1. If any video is currently hovered, it gets exclusive playback priority
    let hoveredItem = null;
    for (const item of registeredVideos.values()) {
        if (item.isHovered) {
            hoveredItem = item;
            break;
        }
    }

    if (hoveredItem) {
        const nextId = hoveredItem.id;
        if (activeVideoId !== nextId) {
            activeVideoId = nextId;
            for (const item of registeredVideos.values()) {
                item.setPlaying(item.id === nextId);
            }
        }
        return;
    }

    // 2. If no video is hovered, find candidate in view screen of the user
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportCenter = viewportHeight / 2;

    let bestCandidate = null;
    let minDistanceToCenter = Infinity;

    for (const item of registeredVideos.values()) {
        if (!item.element || (item.element.isConnected === false)) {
            continue;
        }

        // Must have at least moderate intersection ratio
        if (!item.isIntersecting || item.ratio < 0.25) {
            continue;
        }

        const rect = item.element.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= viewportHeight) {
            continue;
        }

        const elementCenter = (rect.top + rect.bottom) / 2;
        const distanceToCenter = Math.abs(elementCenter - viewportCenter);

        if (distanceToCenter < minDistanceToCenter) {
            minDistanceToCenter = distanceToCenter;
            bestCandidate = item;
        }
    }

    const nextId = bestCandidate ? bestCandidate.id : null;
    if (activeVideoId !== nextId) {
        activeVideoId = nextId;
        for (const item of registeredVideos.values()) {
            item.setPlaying(item.id === nextId);
        }
    }
}

function handleGlobalEvent() {
    scheduleUpdate();
}

function attachGlobalListeners() {
    if (typeof window === 'undefined' || scrollListenerAttached) return;
    scrollListenerAttached = true;
    window.addEventListener('scroll', handleGlobalEvent, { capture: true, passive: true });
    window.addEventListener('resize', handleGlobalEvent, { passive: true });
    document.addEventListener('visibilitychange', handleGlobalEvent);
}

function detachGlobalListenersIfEmpty() {
    if (registeredVideos.size > 0 || !scrollListenerAttached || typeof window === 'undefined') return;
    scrollListenerAttached = false;
    window.removeEventListener('scroll', handleGlobalEvent, { capture: true });
    window.removeEventListener('resize', handleGlobalEvent);
    document.removeEventListener('visibilitychange', handleGlobalEvent);
}

export function registerInViewVideo(id, element, setPlaying) {
    if (!id || !element) return () => {};

    attachGlobalListeners();
    const observer = getSharedObserver();

    const entry = {
        id,
        element,
        ratio: 0,
        isIntersecting: false,
        isHovered: false,
        setPlaying
    };

    registeredVideos.set(id, entry);
    if (observer) {
        observer.observe(element);
    }

    scheduleUpdate();

    return () => {
        if (observer) {
            try {
                observer.unobserve(element);
            } catch (_) {}
        }
        registeredVideos.delete(id);
        if (activeVideoId === id) {
            activeVideoId = null;
            scheduleUpdate();
        }
        detachGlobalListenersIfEmpty();
    };
}

export function setInViewVideoHovered(id, isHovered) {
    const item = registeredVideos.get(id);
    if (!item) return;
    if (item.isHovered === Boolean(isHovered)) return;
    item.isHovered = Boolean(isHovered);
    updateActiveVideo();
}

export function setVideoPlaybackTime(key, seconds) {
    if (!key || typeof seconds !== 'number' || Number.isNaN(seconds)) return;
    videoPlaybackTimes.set(String(key), seconds);
}

export function getVideoPlaybackTime(key) {
    if (!key) return 0;
    const strKey = String(key);
    if (videoPlaybackTimes.has(strKey)) {
        return videoPlaybackTimes.get(strKey) || 0;
    }
    for (const [k, time] of videoPlaybackTimes.entries()) {
        if (k.startsWith(`${strKey}-`) || k.includes(strKey)) {
            return time || 0;
        }
    }
    return 0;
}

// For unit testing only
export function _resetInViewVideoController() {
    if (sharedObserver) {
        try {
            sharedObserver.disconnect();
        } catch (_) {}
        sharedObserver = null;
    }
    registeredVideos.clear();
    videoPlaybackTimes.clear();
    activeVideoId = null;
    if (rafId && typeof window !== 'undefined') {
        window.cancelAnimationFrame(rafId);
        rafId = null;
    }
    detachGlobalListenersIfEmpty();
}

export function _getActiveVideoId() {
    return activeVideoId;
}
